/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common';
import { useEuiMemoizedStyles } from '../../services';
import { Timer } from '../../services/time';
import { EuiGlobalToastListItem } from './global_toast_list_item';
import { EuiToast } from './toast';
import { euiGlobalToastListStyles } from './global_toast_list.styles';
import { EuiButton } from '../button';
import { EuiI18n } from '../i18n';
var sideToClassNameMap = {
  left: 'euiGlobalToastList--left',
  right: 'euiGlobalToastList--right'
};
export var SIDES = keysOf(sideToClassNameMap);
export var TOAST_FADE_OUT_MS = 250;
export var CLEAR_ALL_TOASTS_THRESHOLD_DEFAULT = 3;
export var EuiGlobalToastList = ({
  className,
  toasts = [],
  dismissToast: dismissToastProp,
  toastLifeTimeMs,
  onClearAllToasts,
  side = 'right',
  showClearAllButtonAt = CLEAR_ALL_TOASTS_THRESHOLD_DEFAULT,
  ...rest
}) => {
  const [toastIdToDismissedMap, setToastIdToDismissedMap] = useState({});
  const [toastToDismiss, setToastToDismiss] = useState();
  const prevToasts = useRef([]);
  const dismissTimeoutIds = useRef([]);
  const toastIdToTimerMap = useRef({});
  const isScrollingToBottom = useRef(false);
  const isScrolledToBottom = useRef(true);
  const isUserInteracting = useRef(false);

  // See [Return Value](https://developer.mozilla.org/en-US/docs/Web/API/Window/requestAnimationFrame#Return_value)
  // for information on initial value of 0
  const isScrollingAnimationFrame = useRef(0);
  const startScrollingAnimationFrame = useRef(0);
  const listElement = useRef(null);
  const styles = useEuiMemoizedStyles(euiGlobalToastListStyles);
  const cssStyles = [styles.euiGlobalToastList, styles[side]];
  const startScrollingToBottom = useCallback(() => {
    isScrollingToBottom.current = true;
    const scrollToBottom = () => {
      // Although we cancel the requestAnimationFrame in componentWillUnmount,
      // it's possible for this.listElement to become null in the meantime
      if (!listElement.current) {
        return;
      }
      const position = listElement.current.scrollTop;
      const destination = listElement.current.scrollHeight - listElement.current.clientHeight;
      const distanceToDestination = destination - position;
      if (distanceToDestination < 5) {
        listElement.current.scrollTop = destination;
        isScrollingToBottom.current = false;
        isScrolledToBottom.current = true;
        return;
      }
      listElement.current.scrollTop = position + distanceToDestination * 0.25;
      if (isScrollingToBottom) {
        isScrollingAnimationFrame.current = window.requestAnimationFrame(scrollToBottom);
      }
    };
    startScrollingAnimationFrame.current = window.requestAnimationFrame(scrollToBottom);
  }, []);
  const onMouseEnter = useCallback(() => {
    // Stop scrolling to bottom if we're in mid-scroll, because the user wants to interact with
    // the list.
    isScrollingToBottom.current = false;
    isUserInteracting.current = true;

    // Don't let toasts dismiss themselves while the user is interacting with them.
    for (const toastId in toastIdToTimerMap.current) {
      if (toastIdToTimerMap.current.hasOwnProperty(toastId)) {
        const timer = toastIdToTimerMap.current[toastId];
        timer.pause();
      }
    }
  }, []);
  const onMouseLeave = useCallback(() => {
    isUserInteracting.current = false;
    for (const toastId in toastIdToTimerMap.current) {
      if (toastIdToTimerMap.current.hasOwnProperty(toastId)) {
        const timer = toastIdToTimerMap.current[toastId];
        timer.resume();
      }
    }
  }, []);
  const onScroll = useCallback(() => {
    // Given that this method also gets invoked by the synthetic scroll that happens when a new toast gets added,
    // we want to evaluate if the scroll bottom has been reached only when the user is interacting with the toast,
    // this way we always retain the scroll position the user has set despite adding in new toasts.
    // User interaction is determined through the handler registered for mouseEnter and mouseLeave events.
    if (listElement.current && isUserInteracting.current) {
      isScrolledToBottom.current = listElement.current.scrollHeight - listElement.current.scrollTop === listElement.current.clientHeight;
    }
  }, []);
  const dismissToast = useCallback(toast => {
    // Remove the toast after it's done fading out.
    dismissTimeoutIds.current.push(window.setTimeout(() => {
      setToastToDismiss(toast);
    }, TOAST_FADE_OUT_MS));
    setToastIdToDismissedMap(prev => ({
      ...prev,
      [toast.id]: true
    }));
  }, []);
  const scheduleToastForDismissal = useCallback(toast => {
    // Start fading the toast out once its lifetime elapses.
    toastIdToTimerMap.current[toast.id] = new Timer(() => dismissToast(toast), toast.toastLifeTimeMs != null ? toast.toastLifeTimeMs : toastLifeTimeMs);
  }, [dismissToast, toastLifeTimeMs]);
  const scheduleAllToastsForDismissal = useCallback(() => {
    toasts.forEach(toast => {
      if (!toastIdToTimerMap.current[toast.id]) {
        scheduleToastForDismissal(toast);
      }
    });
  }, [scheduleToastForDismissal, toasts]);

  // componentDidMount
  useEffect(() => {
    const listenerEl = listElement.current;
    if (listenerEl) {
      listenerEl.addEventListener('scroll', onScroll);
      listenerEl.addEventListener('mouseenter', onMouseEnter);
      listenerEl.addEventListener('mouseleave', onMouseLeave);
    }

    // componentWillUnmount
    return () => {
      if (listenerEl) {
        listenerEl.removeEventListener('scroll', onScroll);
        listenerEl.removeEventListener('mouseenter', onMouseEnter);
        listenerEl.removeEventListener('mouseleave', onMouseLeave);
      }
      if (isScrollingAnimationFrame.current !== 0) {
        window.cancelAnimationFrame(isScrollingAnimationFrame.current);
      }
      if (startScrollingAnimationFrame.current !== 0) {
        window.cancelAnimationFrame(startScrollingAnimationFrame.current);
      }
      dismissTimeoutIds.current.forEach(clearTimeout); // eslint-disable-line react-hooks/exhaustive-deps
      for (const toastId in toastIdToTimerMap.current) {
        if (toastIdToTimerMap.current.hasOwnProperty(toastId)) {
          const timer = toastIdToTimerMap.current[toastId]; // eslint-disable-line react-hooks/exhaustive-deps
          timer.clear();
        }
      }
    };
  }, [onMouseEnter, onMouseLeave, onScroll]);

  // componentDidUpdate
  useEffect(() => {
    scheduleAllToastsForDismissal();
    if (!isUserInteracting.current) {
      // If the user has scrolled up the toast list then we don't want to annoy them by scrolling
      // all the way back to the bottom.
      if (isScrolledToBottom.current) {
        if (prevToasts.current.length < toasts.length) {
          startScrollingToBottom();
        }
      }
    }
    prevToasts.current = toasts;
  }, [toasts, scheduleAllToastsForDismissal, startScrollingToBottom]);

  // Toast dismissal side effect
  // Ensure the callback has correct state by not enclosing it in `setTimeout`
  useEffect(() => {
    const toast = toastToDismiss;
    // Because this is triggered by a setTimeout, and because React does not guarantee when
    // state updates happen, it is possible to double-dismiss a toast
    // including by double-clicking the "x" button on the toast
    // so, first check to make sure we haven't already dismissed this toast
    if (toast && toastIdToTimerMap.current.hasOwnProperty(toast.id)) {
      dismissToastProp(toast);
      toastIdToTimerMap.current[toast.id].clear();
      delete toastIdToTimerMap.current[toast.id];
      setToastIdToDismissedMap(prev => {
        const toastIdToDismissedMap = {
          ...prev
        };
        delete toastIdToDismissedMap[toast.id];
        return toastIdToDismissedMap;
      });
    }
  }, [toastToDismiss, dismissToastProp]);
  const renderedToasts = useMemo(() => toasts.map(toast => {
    const {
      text,
      toastLifeTimeMs,
      ...rest
    } = toast;
    const onClose = () => dismissToast(toast);
    return <EuiGlobalToastListItem key={toast.id} isDismissed={toastIdToDismissedMap[toast.id]}>
            <EuiToast onClose={onClose} onFocus={onMouseEnter} onBlur={onMouseLeave} {...rest}>
              {text}
            </EuiToast>
          </EuiGlobalToastListItem>;
  }), [toasts, toastIdToDismissedMap, dismissToast, onMouseEnter, onMouseLeave]);
  const clearAllButton = useMemo(() => {
    if (toasts.length && showClearAllButtonAt && toasts.length >= showClearAllButtonAt) {
      return <EuiI18n key="euiClearAllToasts" tokens={['euiGlobalToastList.clearAllToastsButtonAriaLabel', 'euiGlobalToastList.clearAllToastsButtonDisplayText']} defaults={['Clear all toast notifications', 'Clear all']}>
          {([clearAllToastsButtonAriaLabel, clearAllToastsButtonDisplayText]) => <EuiGlobalToastListItem isDismissed={false}>
              <EuiButton fill color="text" onClick={() => {
            toasts.forEach(toast => dismissToastProp(toast));
            onClearAllToasts?.();
          }} css={styles.euiGlobalToastListDismissButton} aria-label={clearAllToastsButtonAriaLabel} data-test-subj="euiClearAllToastsButton">
                {clearAllToastsButtonDisplayText}
              </EuiButton>
            </EuiGlobalToastListItem>}
        </EuiI18n>;
    }
  }, [showClearAllButtonAt, onClearAllToasts, toasts, dismissToastProp, styles]);
  const classes = classNames('euiGlobalToastList', className);
  return <div aria-live="polite" role="log" ref={listElement} css={cssStyles} className={classes} {...rest}>
      {renderedToasts}
      {clearAllButton}
    </div>;
};
EuiGlobalToastList.propTypes = {
  toasts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.node,
    toastLifeTimeMs: PropTypes.number,
    title: PropTypes.node,
    color: PropTypes.any,
    iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "diff", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "endpoint", "eql", "eraser", "error", "esqlVis", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "index", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "newChat", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipeBreaks", "pipelineApp", "pipeNoBreaks", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),
    onClose: PropTypes.func,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired),
  dismissToast: PropTypes.func.isRequired,
  toastLifeTimeMs: PropTypes.number.isRequired,
  /**
     * Determines which side of the browser window the toasts should appear
     */
  side: PropTypes.oneOf(["right", "left"]),
  /**
     * At this threshold, a "Clear all" button will display at the bottom of the toast list
     * that allows users to dismiss all toasts in a single click.
     *
     * Defaults to `3`. Set to `0` to disable the button entirely.
     */
  showClearAllButtonAt: PropTypes.number,
  /**
     * Optional callback that fires when a user clicks the "Clear all" button.
     */
  onClearAllToasts: PropTypes.func,
  /**
     * Defaults to the [log role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/log_role).
     *
     * The [alert role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role)
     * can be considered only if *all* toasts in this list will require immediate user attention.
     * Several alerts at once, and unnecessary alerts, will a create bad screen reader user experience.
     *
     * @default log
     */
  role: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiGlobalToastList.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/toast/global_toast_list.tsx',
    description: '',
    displayName: 'EuiGlobalToastList',
    methods: [],
    props: {
      toasts: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'toasts',
        parent: {
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        },
        declarations: [{
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        }],
        required: false,
        type: {
          name: 'Toast[]'
        }
      },
      dismissToast: {
        defaultValue: null,
        description: '',
        name: 'dismissToast',
        parent: {
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        },
        declarations: [{
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        }],
        required: true,
        type: {
          name: '(toast: Toast) => void'
        }
      },
      toastLifeTimeMs: {
        defaultValue: null,
        description: '',
        name: 'toastLifeTimeMs',
        parent: {
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        },
        declarations: [{
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      side: {
        defaultValue: {
          value: 'right'
        },
        description: 'Determines which side of the browser window the toasts should appear',
        name: 'side',
        parent: {
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        },
        declarations: [{
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ToastSide',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }]
        }
      },
      showClearAllButtonAt: {
        defaultValue: {
          value: '3'
        },
        description: 'At this threshold, a "Clear all" button will display at the bottom of the toast list\n' + 'that allows users to dismiss all toasts in a single click.\n' + '\n' + 'Defaults to `3`. Set to `0` to disable the button entirely.',
        name: 'showClearAllButtonAt',
        parent: {
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        },
        declarations: [{
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      onClearAllToasts: {
        defaultValue: null,
        description: 'Optional callback that fires when a user clicks the "Clear all" button.',
        name: 'onClearAllToasts',
        parent: {
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        },
        declarations: [{
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      role: {
        defaultValue: {
          value: 'log'
        },
        description: 'Defaults to the [log role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/log_role).\n' + '\n' + 'The [alert role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role)\n' + 'can be considered only if *all* toasts in this list will require immediate user attention.\n' + 'Several alerts at once, and unnecessary alerts, will a create bad screen reader user experience.',
        name: 'role',
        parent: {
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        },
        declarations: [{
          fileName: 'app/src/components/toast/global_toast_list.tsx',
          name: 'EuiGlobalToastListProps'
        }],
        required: false,
        type: {
          name: 'AriaRole'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      }
    },
    extendedInterfaces: ['EuiGlobalToastListProps', 'CommonProps']
  };
} catch (e) {}